import { useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { useClub, useEvents, useNotices } from "../hooks/useClubInfo";
import { MarkerBadge, severityTone } from "../components/MarkerBadge";
import { hasValidLocation } from "../lib/geo";
import type { Club } from "../types";

type Tab = "preise" | "news" | "events" | "kontakt" | "startzeiten";

const baseTabs: { id: Tab; label: string }[] = [
  { id: "preise", label: "Preise" },
  { id: "news", label: "News" },
  { id: "events", label: "Turniere" },
  { id: "kontakt", label: "Kontakt" },
];

export function ClubInfo() {
  const { clubId } = useParams();
  const [searchParams] = useSearchParams();
  const initial = (searchParams.get("tab") as Tab) || "news";
  const [tab, setTab] = useState<Tab>(initial);

  const { club } = useClub(clubId);
  const { notices } = useNotices(clubId);
  const { events } = useEvents(clubId);

  const tabs = club?.teeTimeUrl ? [...baseTabs, { id: "startzeiten" as Tab, label: "Startzeiten" }] : baseTabs;

  return (
    <main className="mx-auto min-h-dvh max-w-md px-5 pb-24 pt-6">
      <Link to={`/club/${clubId}`} className="inline-flex items-center gap-1 text-sm font-medium text-turf-700">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="h-4 w-4">
          <path d="m15 6-6 6 6 6" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
        Zurück zum Dashboard
      </Link>

      <h1 className="mt-4 font-display text-[1.75rem] font-semibold tracking-tight text-turf-900">
        {club?.name ?? "Club-Infos"}
      </h1>

      <div
        role="tablist"
        aria-label="Club-Infos"
        className="mt-5 grid gap-1 rounded-xl bg-chalk-200/70 p-1"
        style={{ gridTemplateColumns: `repeat(${tabs.length}, minmax(0, 1fr))` }}
      >
        {tabs.map((t) => (
          <button
            key={t.id}
            role="tab"
            aria-selected={tab === t.id}
            onClick={() => setTab(t.id)}
            className={`h-10 rounded-lg text-sm font-medium transition-all ${
              tab === t.id ? "bg-white text-turf-800 shadow-sm" : "text-ink-600"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="mt-6 animate-fade-up" key={tab}>
        {tab === "preise" && (
          club?.pricesUrl ? (
            <div className="card rounded-2xl p-6 text-center">
              <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-turf-100 text-turf-700">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" className="h-7 w-7">
                  <path d="M20.6 12.4 12 21 3 12l8.6-8.6H20a1 1 0 0 1 1 1v7.6l-.4.4Z" strokeLinecap="round" strokeLinejoin="round" />
                  <path d="M15.5 8.5h.01" strokeLinecap="round" />
                </svg>
              </div>
              <h2 className="mt-4 font-display text-lg font-semibold text-turf-900">Preise & Konditionen</h2>
              <p className="mt-1 text-sm text-ink-600">Öffnet die Preisliste des Clubs in einem neuen Tab.</p>
              <a
                href={club.pricesUrl}
                target="_blank"
                rel="noreferrer"
                className="mt-5 flex h-12 items-center justify-center rounded-xl bg-turf-700 font-semibold text-chalk-50 shadow-sm transition-transform active:scale-[0.985]"
              >
                Zur Preisliste
              </a>
            </div>
          ) : (
            <ul className="card divide-y divide-chalk-100 overflow-hidden rounded-2xl">
              {club?.prices &&
                Object.entries(club.prices).map(([key, value]) =>
                  value ? (
                    <li key={key} className="flex items-center justify-between px-4 py-3.5">
                      <span className="capitalize text-ink-900">{key}</span>
                      <span className="font-display text-lg font-semibold text-turf-800">{value}</span>
                    </li>
                  ) : null
                )}
              {!club?.prices && <p className="px-4 py-6 text-sm text-ink-400">Keine Preisliste hinterlegt.</p>}
            </ul>
          )
        )}

        {tab === "news" && (
          <ul className="space-y-3">
            {notices.length === 0 && <p className="text-sm text-ink-400">Keine Mitteilungen.</p>}
            {notices.map((n) => (
              <li key={n.id}>
                <Link to={`/club/${clubId}/news/${n.id}`} className="card card-interactive block rounded-2xl p-4">
                  <div className="flex items-center gap-2">
                    <MarkerBadge tone={severityTone(n.severity)} size="sm">
                      {n.severity}
                    </MarkerBadge>
                    <p className="font-semibold text-ink-900">{n.title}</p>
                  </div>
                  <p className="mt-1.5 line-clamp-2 text-sm text-ink-600">{n.text}</p>
                </Link>
              </li>
            ))}
          </ul>
        )}

        {tab === "events" && (
          club?.eventsUrl ? (
            <div className="card rounded-2xl p-6 text-center">
              <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-turf-100 text-turf-700">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" className="h-7 w-7">
                  <path d="M8 21h8M12 17v4M7 4h10v4a5 5 0 0 1-10 0V4ZM7 6H4a3 3 0 0 0 3 5M17 6h3a3 3 0 0 1-3 5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
              <h2 className="mt-4 font-display text-lg font-semibold text-turf-900">Turniere & Events</h2>
              <p className="mt-1 text-sm text-ink-600">Öffnet die Turnierübersicht des Clubs in einem neuen Tab.</p>
              <a
                href={club.eventsUrl}
                target="_blank"
                rel="noreferrer"
                className="mt-5 flex h-12 items-center justify-center rounded-xl bg-turf-700 font-semibold text-chalk-50 shadow-sm transition-transform active:scale-[0.985]"
              >
                Zu den Turnieren
              </a>
            </div>
          ) : (
            <ul className="space-y-3">
              {events.length === 0 && <p className="text-sm text-ink-400">Keine Turniere/Events geplant.</p>}
              {events.map((ev) => (
                <li key={ev.id}>
                  <Link to={`/club/${clubId}/events/${ev.id}`} className="card card-interactive flex items-center gap-4 rounded-2xl p-4">
                    <div className="marker-badge h-13 w-13 flex-shrink-0 flex-col border-0 bg-turf-100 text-turf-700">
                      <span className="text-base font-bold leading-none">
                        {new Date(ev.date).toLocaleDateString("de-DE", { day: "2-digit" })}
                      </span>
                      <span className="text-[10px] uppercase leading-none">
                        {new Date(ev.date).toLocaleDateString("de-DE", { month: "short" })}
                      </span>
                    </div>
                    <span className="font-semibold text-ink-900">{ev.title}</span>
                  </Link>
                </li>
              ))}
            </ul>
          )
        )}

        {tab === "kontakt" && club && (
          <div className="space-y-4">
            <ContactMap club={club} />
            <div className="card divide-y divide-chalk-100 overflow-hidden rounded-2xl">
              <ContactRow label="Adresse" value={club.contact.address} href={mapsUrl(club)} external />
              <ContactRow label="Telefon" value={club.contact.phone} href={`tel:${club.contact.phone}`} />
              <ContactRow label="E-Mail" value={club.contact.email} href={`mailto:${club.contact.email}`} />
              {club.contact.website && (
                <ContactRow label="Website" value={club.contact.website} href={club.contact.website} external />
              )}
            </div>
            {(club.openingHours?.restaurant || club.openingHours?.sekretariat) && (
              <div className="card divide-y divide-chalk-100 overflow-hidden rounded-2xl">
                {club.openingHours?.restaurant && (
                  <HoursRow label="Öffnungszeiten Restaurant" value={club.openingHours.restaurant} />
                )}
                {club.openingHours?.sekretariat && (
                  <HoursRow label="Öffnungszeiten Sekretariat" value={club.openingHours.sekretariat} />
                )}
              </div>
            )}
          </div>
        )}
        {tab === "startzeiten" && club?.teeTimeUrl && (
          <div className="card rounded-2xl p-6 text-center">
            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-turf-100 text-turf-700">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" className="h-7 w-7">
                <circle cx="12" cy="12" r="9" />
                <path d="M12 7v5l3.5 2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
            <h2 className="mt-4 font-display text-lg font-semibold text-turf-900">Startzeit reservieren</h2>
            <p className="mt-1 text-sm text-ink-600">Öffnet das Buchungssystem des Clubs in einem neuen Tab.</p>
            <a
              href={club.teeTimeUrl}
              target="_blank"
              rel="noreferrer"
              className="mt-5 flex h-12 items-center justify-center rounded-xl bg-turf-700 font-semibold text-chalk-50 shadow-sm transition-transform active:scale-[0.985]"
            >
              Zur Startzeitenreservierung
            </a>
          </div>
        )}
      </div>
    </main>
  );
}

function mapsUrl(club: Club): string {
  if (hasValidLocation(club.location?.latitude ?? 0, club.location?.longitude ?? 0)) {
    return `https://www.google.com/maps/search/?api=1&query=${club.location.latitude},${club.location.longitude}`;
  }
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(club.contact.address)}`;
}

function ContactMap({ club }: { club: Club }) {
  const valid = hasValidLocation(club.location?.latitude ?? 0, club.location?.longitude ?? 0);
  const { latitude: lat, longitude: lng } = club.location ?? { latitude: 0, longitude: 0 };
  const dLat = 0.01;
  const dLng = 0.015;

  return (
    <div className="card overflow-hidden rounded-2xl">
      {valid ? (
        <iframe
          title={`Standort von ${club.name}`}
          src={`https://www.openstreetmap.org/export/embed.html?bbox=${lng - dLng}%2C${lat - dLat}%2C${lng + dLng}%2C${lat + dLat}&layer=mapnik&marker=${lat}%2C${lng}`}
          className="h-44 w-full border-0"
          loading="lazy"
        />
      ) : (
        <div className="flex h-24 items-center justify-center text-sm text-ink-400">Keine Koordinaten hinterlegt</div>
      )}
      <a
        href={mapsUrl(club)}
        target="_blank"
        rel="noreferrer"
        className="flex items-center justify-center gap-2 border-t border-chalk-100 py-3 text-sm font-semibold text-turf-700"
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" className="h-4 w-4">
          <path d="M12 21s7-6.5 7-11.5a7 7 0 1 0-14 0C5 14.5 12 21 12 21ZM12 12a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5Z" />
        </svg>
        In Google Maps öffnen
      </a>
    </div>
  );
}

function HoursRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-start gap-3 px-4 py-3.5">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" className="mt-0.5 h-4 w-4 flex-shrink-0 text-turf-600">
        <circle cx="12" cy="12" r="9" />
        <path d="M12 7v5l3.5 2" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
      <div>
        <p className="text-xs uppercase tracking-wide text-ink-400">{label}</p>
        <p className="whitespace-pre-line font-medium text-ink-900">{value}</p>
      </div>
    </div>
  );
}

function ContactRow({
  label,
  value,
  href,
  external,
}: {
  label: string;
  value: string;
  href?: string;
  external?: boolean;
}) {
  return (
    <div className="px-4 py-3.5">
      <p className="text-xs uppercase tracking-wide text-ink-400">{label}</p>
      {href ? (
        <a
          href={href}
          target={external ? "_blank" : undefined}
          rel={external ? "noreferrer" : undefined}
          className="font-medium text-turf-700 underline-offset-2 hover:underline"
        >
          {value}
        </a>
      ) : (
        <p className="font-medium text-ink-900">{value}</p>
      )}
    </div>
  );
}
